"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { registerAction } from "@/server/register-action";
import { signInAction } from "@/server/signin-action";

export default function RegisterPage() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [pending, startTransition] = useTransition();

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await registerAction(fd);
      if (!res.ok) {
        if (res.fieldErrors) setFieldErrors(res.fieldErrors);
        if (res.error) setError(res.error);
        return;
      }
      const signInFd = new FormData();
      signInFd.set("email", fd.get("email") as string);
      signInFd.set("password", fd.get("password") as string);
      const signInRes = await signInAction(signInFd);
      if (signInRes.ok) {
        router.push("/intake");
        router.refresh();
      } else {
        router.push("/login");
      }
    });
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <form
        onSubmit={onSubmit}
        className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-5"
      >
        <h1 className="text-2xl font-bold text-slate-900">Create your account</h1>

        <Field label="Name" name="name" type="text" autoComplete="name" errors={fieldErrors.name} />
        <Field label="Email" name="email" type="email" required autoComplete="email" errors={fieldErrors.email} />
        <Field
          label="Password"
          name="password"
          type="password"
          required
          autoComplete="new-password"
          errors={fieldErrors.password}
          hint="Min 12 chars, with upper, lower, digit, and symbol."
        />

        {error && <p className="text-sm text-rose-600">{error}</p>}

        <button
          type="submit"
          disabled={pending}
          className="w-full py-3 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-50 hover:bg-emerald-700"
        >
          {pending ? "Creating..." : "Create account"}
        </button>

        <p className="text-sm text-center text-slate-600">
          Already have an account?{" "}
          <Link href="/login" className="text-emerald-700 hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </main>
  );
}

function Field(props: {
  label: string;
  name: string;
  type: string;
  required?: boolean;
  autoComplete?: string;
  errors?: string[];
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">
        {props.label}
        {props.required && <span className="text-rose-500"> *</span>}
      </span>
      <input
        name={props.name}
        type={props.type}
        required={props.required}
        autoComplete={props.autoComplete}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
      />
      {props.hint && <span className="block mt-1 text-xs text-slate-500">{props.hint}</span>}
      {props.errors?.map((e) => (
        <span key={e} className="block mt-1 text-xs text-rose-600">{e}</span>
      ))}
    </label>
  );
}
